import type { BuildingType, BuildingView } from './colony-api';
import { CITY_GRID_SCALE, buildingArtMiddle, computeCityGeometry, drawCityScene, hitTestBuilding, isBuildingPlotInteractive, plotCenterCanvas, type CityGeometry } from './colony-city-renderer';
import { t } from '../../engine/i18n/i18n';
import { readThemeColors } from '../worldmap/worldmap-renderer';
import { loadedWallImage, preloadWallArt } from '../../engine/wall-art';
import { loadedBuildingImage, preloadBuildingArt } from '../../engine/building-art';
import { loadedColonyBackgroundImage, preloadColonyBackground } from '../../engine/colony-background';

/**
 * DOM/event wiring for the colony city view canvas. Kept separate from
 * colony-city-renderer.ts's pure geometry/drawing helpers so those stay
 * unit-testable without a real canvas, mirroring the
 * worldmap-renderer.ts / worldmap-screen.ts split.
 *
 * colony-screen.ts's render() fully replaces its container's innerHTML on
 * every data refresh (loadAll / refetchBuildings / refetchRoster /
 * refetchTech), which would destroy a canvas created as a child of that
 * markup. To survive this, the canvas element is created once here (via
 * document.createElement, not via any innerHTML string) and is re-parented
 * into whichever `[data-city-view-host]` element currently exists in the
 * DOM every time `redraw()` runs — the canvas itself, its listeners, and
 * its pixel content are preserved across re-parenting.
 */

/** Pinch/zoom limits (1 = the whole city fitted to its box). On a portrait phone the picture is wider than tall, so it opens zoomed in until it fills the box (see `fillScale`) and the player pans sideways. */
const MIN_VIEW_SCALE = 1;
const MAX_VIEW_SCALE = 2.5;
/** Hard ceiling when the fill-the-box default asks for more than MAX_VIEW_SCALE. */
const MAX_FILL_SCALE = 3.4;
const NARROW_VIEWPORT_PX = 640;
const MAX_BUFFER_DPR = 2;
/** Upper bound on the zoomed buffer's pixel count, so a tall phone box at the fill zoom stays affordable. */
const MAX_BUFFER_PIXELS = 9_000_000;

export interface ColonyCityViewHandle {
  /** Re-draws from the latest data returned by getBuildings(). Safe to call every render()/tick(). */
  redraw(): void;
  /** Removes the canvas and all listeners/observers. Call from the screen's teardown. */
  destroy(): void;
}

/**
 * @param container The screen's stable outer root element (does not itself
 *   get replaced by colony-screen.ts's render() calls) — used as a stable
 *   node to query the current `[data-city-view-host]` placeholder from, and
 *   as the ResizeObserver target (the placeholder div is recreated on every
 *   render(), so it cannot be observed directly).
 */
export function mountColonyCityView(
  container: HTMLElement,
  getBuildings: () => BuildingView[],
  onBuildingClick: (type: BuildingType) => void,
  getBuildingLabel: (type: BuildingType) => string,
  /** Researched tech level, for the renderer's construction-site gate. Omitted means "do not gate on tech". */
  getTechLevel?: () => number,
): ColonyCityViewHandle {
  const canvas = document.createElement('canvas');
  // Sized and positioned from JS (see `applyLayout`) so the zoomed view can be re-rendered crisply and panned.
  // `touch-none`: one finger pans and two pinch once zoomed in, so the browser must not scroll/zoom the page here.
  canvas.className = 'absolute left-0 top-0 block touch-none select-none origin-top-left';
  canvas.setAttribute('aria-label', t('colony.cityAria'));
  canvas.setAttribute('role', 'img');

  // Hover tooltip — a plain DOM element rather than anything canvas-drawn,
  // appended to the body (not `container`) so colony-screen.ts's innerHTML
  // re-renders never wipe it out, same reasoning as the canvas itself being
  // created once here and re-parented rather than recreated. Desktop-only
  // enhancement per this repo's mobile-first convention (CLAUDE.md: hover
  // enhances, never required) — a touch device never fires pointermove
  // before pointerdown, so this simply never appears there, and tap still
  // opens the full building modal exactly as it always has.
  const tooltip = document.createElement('div');
  tooltip.className = 'fixed z-50 px-2 py-1 rounded bg-surface border border-gold-bright/50 text-text text-xs pointer-events-none';
  tooltip.style.display = 'none';
  document.body.appendChild(tooltip);

  // Construction/upgrade countdown badges — one per building currently
  // mid-construction, positioned directly over that building's plot. Same
  // "DOM element appended to body, not canvas-drawn" reasoning as the hover
  // tooltip above: a canvas-drawn badge on a near-row building could be
  // painted over by the wall's front-half art (drawCityScene draws that
  // LAST, after every building, so it occludes near-row buildings by
  // design — see colony-city-renderer.ts), and the requested pulsing-glow
  // treatment is far simpler as a CSS animation than hand-rolled per-frame
  // canvas color interpolation.
  const constructionTimers = new Map<BuildingType, HTMLElement>();

  /** Mirrors colony-screen.ts's own formatCountdown exactly — duplicated rather than imported (colony-screen.ts imports THIS module, so the reverse would be circular), same convention already followed by battle-screen.ts/shop-screen.ts's own private copies. */
  function formatConstructionCountdown(remainingMs: number): string {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
  }

  function constructionTimerEl(type: BuildingType): HTMLElement {
    const existing = constructionTimers.get(type);
    if (existing) return existing;
    const el = document.createElement('div');
    el.className =
      'construction-timer fixed z-40 -translate-x-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded ring-2 ring-gold ring-inset text-[10px] tabular-nums pointer-events-none';
    document.body.appendChild(el);
    constructionTimers.set(type, el);
    return el;
  }

  /** Positions and updates every currently-constructing building's countdown badge; removes any no longer constructing. Called every draw() so it never falls out of sync with the canvas (same per-second cadence as the construction ring itself — see colony-screen.ts's tick()). */
  function syncConstructionTimers(): void {
    const now = Date.now();
    const rect = canvas.getBoundingClientRect();
    const stillConstructing = new Set<BuildingType>();
    for (const building of getBuildings()) {
      if (!building.constructing || !building.constructionEndAt) continue;
      const remainingMs = new Date(building.constructionEndAt).getTime() - now;
      if (remainingMs <= 0) continue; // finalization is lazy/server-side; the next data refresh clears `constructing`
      stillConstructing.add(building.type);
      // The HQ is tall: its footprint centre is far below its body, so its badge goes to the middle of the art.
      const center = building.type === 'COMMAND'
        ? buildingArtMiddle(building.type, building.level, geometry, loadedBuildingImage)
        : plotCenterCanvas(building.type, geometry);
      const el = constructionTimerEl(building.type);
      el.textContent = formatConstructionCountdown(remainingMs);
      const k = screenScale(rect);
      el.style.left = `${rect.left + center.x * k}px`;
      el.style.top = `${rect.top + center.y * k}px`;
    }
    for (const [type, el] of constructionTimers) {
      if (!stillConstructing.has(type)) {
        el.remove();
        constructionTimers.delete(type);
      }
    }
  }

  // jsdom (no `canvas` package installed) returns null here; guard every
  // draw call rather than assume a 2D context is always available.
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    ctx = canvas.getContext('2d');
  } catch {
    ctx = null;
  }

  // ── View transform (pinch zoom + pan) ────────────────────────────────────
  // `rendered` is the zoom the canvas buffer was last drawn at; `live` is the transform on screen. During a gesture
  // only a cheap CSS transform changes (live.s / rendered.s); on release the buffer is re-rendered at live.s.
  const view = { s: 1, tx: 0, ty: 0 };
  let renderedScale = 1;
  let viewInitialised = false;
  /** Once the player pinches/pans, a resize keeps their zoom instead of re-applying the fill default. */
  let userAdjusted = false;
  let maxScale = MAX_VIEW_SCALE;
  const pointers = new Map<number, { x: number; y: number }>();
  let gesture: { kind: 'pan' | 'pinch'; dist: number; s: number; cx: number; cy: number; tx: number; ty: number; moved: boolean } | null = null;
  let suppressClick = false;

  /** On-screen px per logical px, read from the canvas itself so it is right mid-gesture too. */
  function screenScale(rect: DOMRect): number {
    return lastWidth > 0 ? rect.width / lastWidth : 1;
  }

  function clampView(w: number, h: number): void {
    view.s = Math.min(maxScale, Math.max(MIN_VIEW_SCALE, view.s));
    view.tx = Math.min(0, Math.max(w - w * view.s, view.tx));
    view.ty = Math.min(0, Math.max(h - h * view.s, view.ty));
  }

  function applyTransform(): void {
    const k = view.s / renderedScale;
    canvas.style.transform = `translate(${view.tx}px, ${view.ty}px) scale(${k})`;
  }

  let disposed = false;
  let geometry: CityGeometry = computeCityGeometry(0, 0);
  let lastWidth = -1;
  let lastHeight = -1;
  let hoveredType: BuildingType | null = null;

  function host(): HTMLElement | null {
    return container.querySelector('[data-city-view-host]');
  }

  /** Re-parents the persistent canvas into the current host, if it isn't already there. Returns the host, or null if the section isn't mounted right now (e.g. an error screen replaced it). */
  function attach(): HTMLElement | null {
    const h = host();
    if (h && canvas.parentElement !== h) {
      h.appendChild(canvas);
    }
    return h;
  }

  function draw(): void {
    if (disposed || !ctx) return;
    drawCityScene(
      ctx,
      lastWidth,
      lastHeight,
      geometry,
      getBuildings(),
      readThemeColors(),
      loadedWallImage,
      loadedBuildingImage,
      loadedColonyBackgroundImage,
      hoveredType,
      getTechLevel?.() ?? Infinity,
    );
    syncConstructionTimers();
  }

  /** Recomputes geometry and canvas pixel dimensions from the host's current size, then draws. */
  function measureAndResize(): void {
    const h = attach();
    if (!h) return;
    const width = h.clientWidth;
    const height = h.clientHeight;
    lastWidth = width;
    lastHeight = height;
    geometry = computeCityGeometry(width, height);
    if ((!viewInitialised || !userAdjusted) && width > 0 && height > 0) {
      // Phones open zoomed in far enough that the picture fills the box (cover, not contain). The picture is a fixed
      // 2:1 slab, so in a portrait box that means its sides run off-screen and the player pans/pinches. Wider
      // screens already fill by height, so this comes out at 1 (fit).
      const pictureHeight = geometry.displayHeight / CITY_GRID_SCALE;
      const fill = width < NARROW_VIEWPORT_PX && pictureHeight > 0 ? Math.min(MAX_FILL_SCALE, Math.max(1, height / pictureHeight)) : 1;
      maxScale = Math.max(MAX_VIEW_SCALE, fill);
      view.s = fill;
      view.tx = (width - width * view.s) / 2;
      view.ty = (height - height * view.s) / 2;
      viewInitialised = true;
    }
    clampView(width, height);
    renderedScale = view.s;
    canvas.style.width = `${width * renderedScale}px`;
    canvas.style.height = `${height * renderedScale}px`;
    if (ctx) {
      const cssPixels = width * renderedScale * height * renderedScale;
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_BUFFER_DPR, Math.sqrt(MAX_BUFFER_PIXELS / Math.max(1, cssPixels)));
      canvas.width = Math.max(1, Math.round(width * renderedScale * dpr));
      canvas.height = Math.max(1, Math.round(height * renderedScale * dpr));
      ctx.setTransform(dpr * renderedScale, 0, 0, dpr * renderedScale, 0, 0);
    }
    applyTransform();
    draw();
  }

  function redraw(): void {
    if (disposed) return;
    const h = attach();
    if (!h) return;
    // Only re-measure (and reset the canvas's pixel buffer) when the host's
    // size actually changed — a data-only re-render shouldn't force a full
    // resize every tick, but a layout shift must not leave hit-testing
    // pointed at stale geometry.
    if (h.clientWidth !== lastWidth || h.clientHeight !== lastHeight) {
      measureAndResize();
    } else {
      draw();
    }
  }

  function isInteractive(type: BuildingType): boolean {
    const buildings = getBuildings();
    const building = buildings.find((b) => b.type === type);
    const level = building?.level ?? 0;
    const hqLevel = buildings.find((b) => b.type === 'COMMAND')?.level ?? 10;
    const techLevel = getTechLevel?.() ?? Infinity;
    return isBuildingPlotInteractive(type, level, hqLevel, techLevel);
  }

  function onClick(e: MouseEvent): void {
    if (suppressClick) {
      suppressClick = false;
      return; // the pointer was dragged (pan/pinch), not tapped
    }
    const rect = canvas.getBoundingClientRect();
    const k = screenScale(rect);
    const x = (e.clientX - rect.left) / k;
    const y = (e.clientY - rect.top) / k;
    const type = hitTestBuilding(x, y, geometry);
    if (type) onBuildingClick(type);
  }
  canvas.addEventListener('click', onClick);

  // ── Gestures ─────────────────────────────────────────────────────────────
  const GESTURE_SLOP_PX = 6;

  function beginGesture(): void {
    const pts = [...pointers.values()];
    const rect = host()?.getBoundingClientRect() ?? canvas.getBoundingClientRect();
    if (pts.length >= 2) {
      const [a, b] = pts;
      gesture = {
        kind: 'pinch',
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        s: view.s,
        cx: (a.x + b.x) / 2 - rect.left,
        cy: (a.y + b.y) / 2 - rect.top,
        tx: view.tx,
        ty: view.ty,
        moved: true,
      };
      suppressClick = true;
    } else if (pts.length === 1) {
      gesture = { kind: 'pan', dist: 0, s: view.s, cx: pts[0].x, cy: pts[0].y, tx: view.tx, ty: view.ty, moved: false };
    }
  }

  function updateGesture(): void {
    if (!gesture) return;
    const pts = [...pointers.values()];
    const w = lastWidth;
    const h = lastHeight;
    if (gesture.kind === 'pinch' && pts.length >= 2) {
      const rect = host()?.getBoundingClientRect() ?? canvas.getBoundingClientRect();
      const [a, b] = pts;
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const mx = (a.x + b.x) / 2 - rect.left;
      const my = (a.y + b.y) / 2 - rect.top;
      const s = Math.min(maxScale, Math.max(MIN_VIEW_SCALE, gesture.s * (dist / gesture.dist)));
      // Keep the content point that started under the fingers' midpoint under their midpoint now.
      const contentX = (gesture.cx - gesture.tx) / gesture.s;
      const contentY = (gesture.cy - gesture.ty) / gesture.s;
      view.s = s;
      view.tx = mx - contentX * s;
      view.ty = my - contentY * s;
    } else if (gesture.kind === 'pan' && pts.length === 1) {
      const dx = pts[0].x - gesture.cx;
      const dy = pts[0].y - gesture.cy;
      if (!gesture.moved && Math.hypot(dx, dy) < GESTURE_SLOP_PX) return;
      if (view.s <= MIN_VIEW_SCALE + 0.001) return; // nothing to pan at fit
      gesture.moved = true;
      suppressClick = true;
      hideTooltip();
      view.tx = gesture.tx + dx;
      view.ty = gesture.ty + dy;
    }
    userAdjusted = true;
    clampView(w, h);
    applyTransform();
    syncConstructionTimers();
  }

  function endGesture(): void {
    gesture = null;
    // Re-render crisply at the zoom the user settled on.
    if (Math.abs(view.s - renderedScale) > 0.001) measureAndResize();
    else syncConstructionTimers();
    // Anything still down (the second finger of a pinch lifting first) carries on as a pan.
    if (pointers.size > 0) beginGesture();
  }

  function onPointerDown(e: PointerEvent): void {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    canvas.setPointerCapture?.(e.pointerId);
    suppressClick = false;
    beginGesture();
  }
  function onPointerUp(e: PointerEvent): void {
    if (!pointers.delete(e.pointerId)) return;
    if (pointers.size === 0 || gesture?.kind === 'pinch') endGesture();
    else gesture = null;
  }
  /** Zooms to `scale` keeping the content point under (mx, my) — host-relative px — where it is, then re-renders crisply once the zooming settles. */
  function zoomAbout(mx: number, my: number, scale: number): void {
    const s = Math.min(maxScale, Math.max(MIN_VIEW_SCALE, scale));
    const contentX = (mx - view.tx) / view.s;
    const contentY = (my - view.ty) / view.s;
    view.s = s;
    view.tx = mx - contentX * s;
    view.ty = my - contentY * s;
    userAdjusted = true;
    clampView(lastWidth, lastHeight);
    applyTransform();
    syncConstructionTimers();
    if (wheelTimer) clearTimeout(wheelTimer);
    wheelTimer = setTimeout(() => { wheelTimer = null; if (!disposed && Math.abs(view.s - renderedScale) > 0.001) measureAndResize(); }, 150);
  }
  function onWheel(e: WheelEvent): void {
    // The colony screen never scrolls under the pointer (the canvas host is overflow:hidden and the data column is a
    // sibling), so a plain wheel zooms — a mouse has no pinch. A trackpad pinch arrives as ctrl+wheel with finer
    // deltas, hence the larger factor for it.
    e.preventDefault();
    const rect = host()?.getBoundingClientRect() ?? canvas.getBoundingClientRect();
    zoomAbout(e.clientX - rect.left, e.clientY - rect.top, view.s * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015)));
  }
  let wheelTimer: ReturnType<typeof setTimeout> | null = null;

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerUp);
  canvas.addEventListener('wheel', onWheel, { passive: false });

  const TOOLTIP_OFFSET_PX = 14;
  function positionTooltip(clientX: number, clientY: number): void {
    tooltip.style.left = `${clientX + TOOLTIP_OFFSET_PX}px`;
    tooltip.style.top = `${clientY + TOOLTIP_OFFSET_PX}px`;
  }

  function hideTooltip(): void {
    tooltip.style.display = 'none';
  }

  function onPointerMove(e: PointerEvent): void {
    if (pointers.has(e.pointerId)) {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (gesture) {
        updateGesture();
        return;
      }
    }
    const rect = canvas.getBoundingClientRect();
    const k = screenScale(rect);
    const x = (e.clientX - rect.left) / k;
    const y = (e.clientY - rect.top) / k;
    const rawType = hitTestBuilding(x, y, geometry);
    const type = rawType && isInteractive(rawType) ? rawType : null;
    if (type !== hoveredType) {
      hoveredType = type;
      canvas.classList.toggle('cursor-interactive', !!type);
      draw();
    }
    if (type) {
      tooltip.textContent = getBuildingLabel(type);
      tooltip.style.display = 'block';
      positionTooltip(e.clientX, e.clientY);
    } else {
      hideTooltip();
    }
  }
  function onPointerLeave(): void {
    if (hoveredType !== null) {
      hoveredType = null;
      canvas.classList.remove('cursor-interactive');
      draw();
    }
    hideTooltip();
  }
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerleave', onPointerLeave);

  window.addEventListener('resize', measureAndResize);
  let resizeObserver: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => measureAndResize());
    resizeObserver.observe(container);
  }

  measureAndResize();
  // A cache hit (the common case after the very first mount anywhere in the
  // page's lifetime) draws immediately above via measureAndResize(); this
  // only needs to trigger a redraw for tiers that genuinely just finished
  // their first-ever load.
  preloadWallArt(() => {
    if (!disposed) draw();
  });
  preloadBuildingArt(() => {
    if (!disposed) draw();
  });
  preloadColonyBackground(() => {
    if (!disposed) draw();
  });

  return {
    redraw,
    destroy(): void {
      if (disposed) return;
      disposed = true;
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      canvas.removeEventListener('wheel', onWheel);
      if (wheelTimer) clearTimeout(wheelTimer);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('resize', measureAndResize);
      resizeObserver?.disconnect();
      resizeObserver = null;
      canvas.remove();
      tooltip.remove();
      for (const el of constructionTimers.values()) el.remove();
      constructionTimers.clear();
    },
  };
}
